import React, { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { todayISO, formatEuro, formatDate } from "../lib/format";
import { Trash2 } from "lucide-react";

/**
 * Update asset value + view history dialog.
 */
export default function AssetValueDialog({ open, onOpenChange, asset, onSaved }) {
  const [value, setValue] = useState("");
  const [date, setDate] = useState(todayISO());
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadHistory = async () => {
    if (!asset) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/assets/${asset.id}/values`);
      setHistory(data.values || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && asset) {
      setValue(asset.current_value != null ? String(asset.current_value) : "");
      setDate(todayISO());
      setNotes("");
      loadHistory();
    }
    // eslint-disable-next-line
  }, [open, asset]);

  if (!asset) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/assets/${asset.id}/values`, {
        value: Number(value),
        update_date: date,
        notes,
      });
      toast.success("Value updated");
      onSaved?.();
      await loadHistory();
      setNotes("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/assets/${asset.id}/values/${id}`);
      toast.success("History entry removed");
      onSaved?.();
      await loadHistory();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white border-[#E5E3DB] rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="asset-value-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1D3328]">Update value</DialogTitle>
          <DialogDescription className="text-[#5C635A]">
            <span className="font-medium text-[#1D3328]">{asset.name}</span>
            {" · "}current{" "}
            <span className="num font-medium text-[#1D3328]">{formatEuro(asset.current_value)}</span>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="grid grid-cols-2 gap-3" data-testid="asset-value-form">
          <div className="space-y-1.5 col-span-2">
            <Label className="text-xs tracking-widest uppercase text-[#8B9389]">New value (€)</Label>
            <Input
              type="number" step="0.01" min="0" required
              value={value} onChange={(e) => setValue(e.target.value)}
              className="rounded-xl border-[#E5E3DB] num h-11"
              data-testid="asset-value-amount"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Date</Label>
            <Input
              type="date" required value={date}
              onChange={(e) => setDate(e.target.value)}
              className="rounded-xl border-[#E5E3DB] h-11"
              data-testid="asset-value-date"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Notes</Label>
            <Input
              value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Monthly check"
              className="rounded-xl border-[#E5E3DB] h-11"
              data-testid="asset-value-notes"
            />
          </div>
          <Button type="submit" disabled={saving}
                  className="col-span-2 rounded-xl bg-[#2C4A3B] hover:bg-[#3A5F4C] text-white"
                  data-testid="asset-value-submit">
            {saving ? "Saving…" : "Record update"}
          </Button>
        </form>

        <div className="mt-2">
          <p className="label-kicker">History</p>
          {loading ? (
            <p className="text-xs text-[#8B9389] mt-2">Loading…</p>
          ) : history.length === 0 ? (
            <p className="text-xs text-[#8B9389] mt-2">No history yet.</p>
          ) : (
            <ul className="divide-y divide-[#EEEBE2] mt-2 max-h-48 overflow-y-auto">
              {history.map((h) => (
                <li key={h.id} className="py-2 flex items-center justify-between text-sm" data-testid="asset-value-row">
                  <div>
                    <span className="num font-medium text-[#1D3328]">{formatEuro(h.value)}</span>
                    <span className="text-xs text-[#8B9389] ml-2">{formatDate(h.update_date)}</span>
                    {h.notes && <span className="text-xs text-[#5C635A] ml-2">· {h.notes}</span>}
                  </div>
                  <button onClick={() => remove(h.id)}
                          className="text-xs text-[#B04A32] hover:text-[#8A3823]"
                          data-testid="asset-value-delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  className="rounded-xl border-[#E5E3DB]"
                  data-testid="asset-value-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
